/**
 * Runtime citation cross-linking — the "intertwined" law layer.
 *
 * Ties the three offline collections together at read time:
 *   • MGL statutes (via the MGL search singleton + curated CASELAW statutes)
 *   • Court rules  (the offline `rules` table)
 *   • Case law     (the offline verbatim `cases` table + curated CASELAW)
 *
 * `linkifyToHtml(text)` scans any passage, turns every resolvable statute /
 * rule / case citation into a clickable link, and `openCitation(kind, key)`
 * opens the cited authority in a modal. `citedByHtml(docId)` renders the
 * reverse direction ("Cited by …") from the build-time citation graph.
 *
 * Everything runs on-device; the indexes are built once from the shared
 * CaselawDB and the in-bundle CASELAW list, then cached for the session.
 */
import type { CaselawEntry } from '@/types';
import type { CitingDoc, JuryResearchHit } from '@/data/caselaw-db';
import {
  extractCitations,
  resolveCitation,
  statuteKey,
  ruleKey,
  ruleSetSlug,
  type CiteIndexes,
  type CiteKind,
  type ResolvedTarget
} from '@/core/cite-resolve';
import { parseCitation } from '@/core/citations';
import { CASELAW } from '@/data/caselaw';
import { getCaselawDb } from '@/data/caselaw-db-instance';
import { loadMglSearch } from '@/app/mgl';
import { showModal, showToast } from '@/ui/overlays';
import { escapeHTML } from '@/utils/formatters';

/* ------------------------------------------------------------- index build */

let indexPromise: Promise<CiteIndexes> | null = null;

/** Build (once) the key→target indexes across all three collections. */
function buildIndexes(): Promise<CiteIndexes> {
  if (indexPromise) return indexPromise;
  indexPromise = (async () => {
    const statutes = new Map<string, ResolvedTarget>();
    const rules = new Map<string, ResolvedTarget>();
    const cases = new Map<string, ResolvedTarget>();

    // Curated CASELAW first (rich quick-reference entries).
    for (const e of CASELAW) {
      if (e.type === 'statute') {
        const p = parseCitation(e.citation);
        if (p) statutes.set(statuteKey(p.chapter, p.section), curated(e));
      } else if (e.type === 'caselaw') {
        for (const m of extractCitations(e.citation)) {
          if (m.kind === 'case') cases.set(m.key, curated(e));
        }
      } else if (e.type === 'rule') {
        for (const m of extractCitations(e.citation)) {
          if (m.kind === 'rule') rules.set(m.key, curated(e));
        }
      }
    }

    // DB verbatim docs override curated for cases + rules (authoritative full text).
    const db = await getCaselawDb();
    if (db) {
      for (const r of db.allRuleRefs()) {
        const slug = r.ruleSet ? ruleSetSlug(r.ruleSet) : null;
        const num = (r.ruleNumber ?? '').replace(/^Rule\s+/i, '').trim();
        if (slug && num) {
          rules.set(ruleKey(slug, num), {
            kind: 'rule',
            docId: r.docId,
            title: [r.ruleNumber, r.title].filter(Boolean).join(' — '),
            source: 'db'
          });
        }
      }
      for (const c of db.allCaseRefs()) {
        for (const m of extractCitations(c.citation ?? '')) {
          if (m.kind === 'case') {
            cases.set(m.key, { kind: 'case', docId: c.docId, title: c.name, source: 'db' });
          }
        }
      }
    }
    return { statutes, rules, cases };
  })();
  return indexPromise;
}

function curated(e: CaselawEntry): ResolvedTarget {
  const kind: CiteKind = e.type === 'caselaw' ? 'case' : e.type;
  return { kind, docId: e.id, title: e.citation, source: 'curated' };
}

/* ----------------------------------------------------------------- linkify */

/**
 * Turn `text` into HTML with each resolvable citation wrapped in a clickable
 * link. Statute citations are always linked (MGL is near-complete); rule and
 * case citations are linked only when they resolve to a doc we actually hold.
 * Newlines become <br>.
 */
export async function linkifyToHtml(text: string): Promise<string> {
  const idx = await buildIndexes();
  return linkifySync(text, idx);
}

function linkifySync(text: string, idx: CiteIndexes): string {
  const matches = extractCitations(text);
  let html = '';
  let pos = 0;
  for (const m of matches) {
    html += escapeHTML(text.slice(pos, m.index));
    const linkable = m.kind === 'statute' ? true : !!resolveCitation(m, idx);
    html += linkable
      ? `<a onclick="openCitation('${m.kind}','${escapeHTML(m.key)}')" ` +
        'style="color:var(--blue-primary);text-decoration:underline;text-decoration-style:dotted;text-underline-offset:2px;cursor:pointer;" ' +
        `title="Open cited authority">${escapeHTML(m.raw)}</a>`
      : escapeHTML(m.raw);
    pos = m.index + m.length;
  }
  html += escapeHTML(text.slice(pos));
  return html.replace(/\n/g, '<br>');
}

/**
 * Enhance an already-rendered element in place: turn every resolvable citation
 * in its text nodes into a clickable cross-link. Composes with existing markup
 * (e.g. <mark> highlight spans) because it only rewrites raw text nodes and
 * skips anything already inside a link or button.
 */
export async function linkifyElement(root: HTMLElement): Promise<void> {
  const idx = await buildIndexes();
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      const v = node.nodeValue;
      if (!v || !v.trim()) return NodeFilter.FILTER_REJECT;
      let p = node.parentElement;
      while (p && p !== root) {
        if (p.tagName === 'A' || p.tagName === 'BUTTON') return NodeFilter.FILTER_REJECT;
        p = p.parentElement;
      }
      return NodeFilter.FILTER_ACCEPT;
    }
  });
  const nodes: Text[] = [];
  let n: Node | null;
  while ((n = walker.nextNode())) nodes.push(n as Text);

  for (const textNode of nodes) {
    const text = textNode.nodeValue as string;
    const matches = extractCitations(text).filter(
      (m) => m.kind === 'statute' || !!resolveCitation(m, idx)
    );
    if (!matches.length) continue;
    const frag = document.createDocumentFragment();
    let pos = 0;
    for (const m of matches) {
      if (m.index > pos) frag.appendChild(document.createTextNode(text.slice(pos, m.index)));
      const a = document.createElement('a');
      a.textContent = m.raw;
      a.style.cssText =
        'color:var(--blue-primary);text-decoration:underline;text-decoration-style:dotted;text-underline-offset:2px;cursor:pointer;';
      a.title = 'Open cited authority';
      const kind = m.kind;
      const key = m.key;
      a.addEventListener('click', (e) => {
        e.preventDefault();
        openCitation(kind, key);
      });
      frag.appendChild(a);
      pos = m.index + m.length;
    }
    if (pos < text.length) frag.appendChild(document.createTextNode(text.slice(pos)));
    textNode.parentNode?.replaceChild(frag, textNode);
  }
}

/* -------------------------------------------------------------- open a cite */

/** Open the authority a citation link points to (called from linkified HTML). */
export function openCitation(kind: CiteKind, key: string): void {
  void (async () => {
    const idx = await buildIndexes();
    if (kind === 'rule') {
      const t = idx.rules?.get(key);
      if (!t?.docId) return void showToast('That rule isn’t in the offline library.');
      if (t.source === 'db') (await import('./law-rules')).openRule(t.docId);
      else openCuratedModal(t.docId);
    } else if (kind === 'case') {
      const t = idx.cases?.get(key);
      if (!t?.docId) return void showToast('That opinion isn’t in the offline library.');
      if (t.source === 'db') (await import('./law-opinions')).openOpinion(t.docId);
      else openCuratedModal(t.docId);
    } else {
      const t = idx.statutes?.get(key);
      if (t?.docId) return openCuratedModal(t.docId);
      void openMglStatute(key);
    }
  })();
}

/** Render a curated CASELAW entry (statute/case/rule quick reference) in a modal. */
function openCuratedModal(id: string): void {
  const e = CASELAW.find((x) => x.id === id);
  if (!e) return void showToast('Authority not found.');
  void (async () => {
    const bodyHtml = e.fullText ? await linkifyToHtml(e.fullText) : '';
    // Statutes get a "Cited by" panel keyed on their normalized statute key.
    let citedBy = '';
    if (e.type === 'statute') {
      const p = parseCitation(e.citation);
      if (p) citedBy = await citedByStatuteHtml(statuteKey(p.chapter, p.section));
    }
    const holdings = (e.holdings || [])
      .map(
        (h) =>
          `<li style="font-size:11px;color:#1e293b;line-height:1.6;margin-bottom:4px;">${escapeHTML(h)}</li>`
      )
      .join('');
    showModal(
      `<div style="font-family:Georgia,serif;font-weight:600;font-size:15px;color:#1e293b;margin-bottom:2px;">${escapeHTML(e.citation)}</div>` +
        `<div style="font-size:11px;margin:6px 0 10px;">` +
        (e.url
          ? `<a href="${escapeHTML(e.url)}" target="_blank" rel="noopener" style="color:var(--blue-primary);font-weight:600;text-decoration:none;">↗ Open official source</a>`
          : '') +
        '</div>' +
        `<div style="background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:9px 11px;margin-bottom:10px;font-size:11px;color:#78350f;line-height:1.6;">⚠ Curated quick reference — verify each citation and holding against the official reporter.</div>` +
        (e.summary
          ? `<div style="background:#eff6ff;border:1px solid #bfdbfe;border-radius:8px;padding:10px 12px;margin-bottom:10px;font-size:12px;color:#1e3a5f;line-height:1.7;">${escapeHTML(e.summary)}</div>`
          : '') +
        citedBy +
        (holdings ? `<ul style="margin:0 0 10px;padding-left:18px;">${holdings}</ul>` : '') +
        (bodyHtml
          ? `<div style="max-height:55vh;overflow:auto;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;font-family:Georgia,serif;font-size:13px;line-height:1.75;color:#1e293b;">${bodyHtml}</div>`
          : '')
    );
  })();
}

/** Split a statute key like "234A-67" into { chapter, section }. */
function splitStatuteKey(key: string): { chapter: string; section: string } | null {
  const m = key.match(/^([0-9A-Z]+)-(.+)$/);
  return m ? { chapter: m[1], section: m[2] } : null;
}

/** Resolve a statute citation against the offline MGL library and show it. */
async function openMglStatute(key: string): Promise<void> {
  const parts = splitStatuteKey(key);
  if (!parts) return void showToast('Statute citation not recognized.');
  const mgl = await loadMglSearch();
  if (!mgl) return void showToast('Statute library unavailable offline.');
  const section = await mgl.getSection(parts.chapter, parts.section);
  if (!section) {
    return void showToast(`G.L. c. ${parts.chapter}, § ${parts.section} not found offline.`);
  }
  const bodyHtml = await linkifyToHtml(section.text || '');
  const citedBy = await citedByStatuteHtml(key);
  showModal(
    `<div style="font-family:Georgia,serif;font-weight:600;font-size:15px;color:#1e293b;margin-bottom:2px;">${escapeHTML(section.citation)}</div>` +
      (section.heading
        ? `<div style="font-size:12px;color:#64748b;margin-bottom:4px;">${escapeHTML(section.heading)}</div>`
        : '') +
      `<div style="font-size:11px;margin:6px 0 10px;">` +
      (section.url
        ? `<a href="${escapeHTML(section.url)}" target="_blank" rel="noopener" style="color:var(--blue-primary);font-weight:600;text-decoration:none;">↗ Source: malegislature.gov</a>`
        : '') +
      '</div>' +
      citedBy +
      `<div style="max-height:60vh;overflow:auto;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:14px;font-family:Georgia,serif;font-size:13px;line-height:1.75;color:#1e293b;">${bodyHtml}</div>`
  );
}

/* --------------------------------------------------------------- cited by */

const KIND_LABEL: Record<string, string> = { case: 'Case', rule: 'Rule', statute: 'Statute' };
const KIND_COLOR: Record<string, string> = { case: '#1d4ed8', rule: '#7c3aed', statute: '#065f46' };

/**
 * Render the "Cited by" panel for a doc: the opinions/rules in the offline
 * library that cite it. Returns '' when nothing cites it.
 */
export async function citedByHtml(docId: string): Promise<string> {
  const db = await getCaselawDb();
  if (!db) return '';
  try {
    return renderCitedBy(db.citedBy(docId)) + renderResearchDiscussing(db.researchCitingDoc(docId));
  } catch {
    return '';
  }
}

/** "Cited by" panel for a statute (matched by its normalized key, e.g. "234A-67"). */
export async function citedByStatuteHtml(key: string): Promise<string> {
  const db = await getCaselawDb();
  if (!db) return '';
  try {
    return (
      renderCitedBy(db.citedByStatute(key)) + renderResearchDiscussing(db.researchCitingStatute(key))
    );
  } catch {
    return '';
  }
}

/**
 * Cross-reference panel: jury-selection research findings that discuss the
 * authority being viewed. Collapsible; '' when none.
 */
function renderResearchDiscussing(hits: JuryResearchHit[]): string {
  if (!hits.length) return '';
  const rows = hits
    .map(
      (h) =>
        `<div style="background:#fff;border:1px solid #e9d8fd;border-radius:7px;padding:7px 9px;margin-bottom:5px;">` +
        `<div style="font-size:11px;font-weight:700;color:#1e293b;">${escapeHTML(h.topic || '')}` +
        `<span style="display:inline-block;background:#7c3aed;color:#fff;border-radius:4px;padding:0 5px;font-size:8px;font-weight:800;vertical-align:middle;margin-left:6px;">${escapeHTML(h.domain)}</span></div>` +
        (h.summary
          ? `<div style="font-size:10.5px;color:#475569;line-height:1.5;margin-top:3px;">${escapeHTML(h.summary)}</div>`
          : '') +
        '</div>'
    )
    .join('');
  return (
    `<details style="background:#faf5ff;border:1px solid #e9d8fd;border-radius:8px;padding:8px 11px;margin-bottom:12px;">` +
    `<summary style="font-size:10px;font-weight:700;color:#6b21a8;text-transform:uppercase;letter-spacing:.4px;cursor:pointer;">Discussed in ${hits.length} jury-selection research finding${hits.length === 1 ? '' : 's'}</summary>` +
    `<div style="margin-top:8px;">${rows}</div>` +
    '</details>'
  );
}

/** Shared renderer for a list of citing docs. */
function renderCitedBy(citing: CitingDoc[]): string {
  if (!citing.length) return '';
  const rows = citing
    .map((c) => {
      const kind = c.kind === 'case' ? 'case' : 'rule';
      const openFn = kind === 'case' ? 'openOpinionById' : 'openRuleById';
      const badge = KIND_LABEL[c.kind] || c.kind;
      const color = KIND_COLOR[c.kind] || '#334155';
      return (
        `<button onclick="${openFn}('${escapeHTML(c.docId)}')" style="display:block;width:100%;text-align:left;background:#fff;border:1px solid #e2e8f0;border-radius:7px;padding:7px 9px;margin-bottom:5px;cursor:pointer;">` +
        `<span style="display:inline-block;background:${color};color:#fff;border-radius:4px;padding:0 5px;font-size:9px;font-weight:700;vertical-align:middle;margin-right:6px;">${badge}</span>` +
        `<span style="font-size:12px;font-weight:600;color:#1e293b;">${escapeHTML(c.title)}</span>` +
        (c.sub ? `<span style="font-size:10px;color:#94a3b8;margin-left:6px;">${escapeHTML(c.sub)}</span>` : '') +
        '</button>'
      );
    })
    .join('');
  return (
    `<details style="background:#f1f5f9;border:1px solid #e2e8f0;border-radius:8px;padding:8px 11px;margin-bottom:12px;">` +
    `<summary style="font-size:10px;font-weight:700;color:#475569;text-transform:uppercase;letter-spacing:.4px;cursor:pointer;">Cited by ${citing.length} in this library</summary>` +
    `<div style="margin-top:8px;">${rows}</div>` +
    '</details>'
  );
}

/* ------------------------------------------- case law for a criminal statute */

/**
 * Connect a criminal statute to case law. Renders (a) the opinions and rules in
 * the offline library that cite G.L. c.<chapter> § <section>, from the build-
 * time citation graph, and (b) authoritative external searches for that exact
 * citation — so every statute reaches on-point case law even when the offline
 * jury-selection corpus has none. Returns '' only if the DB can't be opened.
 */
export async function caseLawForStatuteHtml(chapter: string, section: string): Promise<string> {
  const key = statuteKey(chapter, section);
  let citing: CitingDoc[] = [];
  const db = await getCaselawDb();
  if (db) {
    try {
      citing = db.citedByStatute(key);
    } catch {
      citing = [];
    }
  }
  const cases = citing.filter((c) => c.kind === 'case');
  const rules = citing.filter((c) => c.kind === 'rule');

  const openBtn = (c: CitingDoc): string => {
    const openFn = c.kind === 'case' ? 'openOpinionById' : 'openRuleById';
    const badge = KIND_LABEL[c.kind] || c.kind;
    const color = KIND_COLOR[c.kind] || '#334155';
    return (
      `<button onclick="${openFn}('${escapeHTML(c.docId)}')" style="display:block;width:100%;text-align:left;background:#fff;border:1px solid #dbeafe;border-radius:7px;padding:7px 9px;margin-bottom:5px;cursor:pointer;">` +
      `<span style="display:inline-block;background:${color};color:#fff;border-radius:4px;padding:0 5px;font-size:9px;font-weight:700;vertical-align:middle;margin-right:6px;">${badge}</span>` +
      `<span style="font-size:12px;font-weight:600;color:#1e293b;">${escapeHTML(c.title)}</span>` +
      (c.sub ? `<span style="font-size:10px;color:#94a3b8;margin-left:6px;">${escapeHTML(c.sub)}</span>` : '') +
      '</button>'
    );
  };

  // Build-time offline citers (verbatim opinions + rules we actually hold).
  let offline = '';
  if (cases.length || rules.length) {
    offline =
      `<div style="font-size:10px;font-weight:700;color:#1e3a8a;text-transform:uppercase;letter-spacing:.4px;margin-bottom:5px;">In this offline library (${cases.length + rules.length})</div>` +
      cases.map(openBtn).join('') +
      rules.map(openBtn).join('');
  } else {
    offline = `<div style="font-size:11px;color:#64748b;margin-bottom:6px;">No opinion in the offline jury-selection library cites this section — search authoritative sources:</div>`;
  }

  // Always-available authoritative external searches for the exact citation.
  const scholar =
    'https://scholar.google.com/scholar?as_sdt=4&q=' +
    encodeURIComponent(`"c. ${chapter}, § ${section}" Massachusetts`);
  const courtlistener =
    'https://www.courtlistener.com/?type=o&order_by=score+desc&court=mass+massappct&q=' +
    encodeURIComponent(`"${chapter}, § ${section}"`);
  const extLink = (href: string, label: string): string =>
    `<a href="${href}" target="_blank" rel="noopener" style="display:inline-block;background:#1d4ed8;color:#fff;border-radius:6px;padding:5px 10px;font-size:11px;font-weight:600;text-decoration:none;margin:0 6px 6px 0;">${label} ↗</a>`;

  const external =
    `<div style="margin-top:2px;">` +
    extLink(scholar, 'Google Scholar') +
    extLink(courtlistener, 'CourtListener') +
    `<div style="font-size:10px;color:#94a3b8;line-height:1.5;">Opens on-point Massachusetts opinions construing this statute (needs internet).</div>` +
    `</div>`;

  return (
    `<div style="margin-top:9px;border-top:1px solid #dbeafe;padding-top:8px;">` +
    `<div style="font-size:11px;font-weight:700;color:#1e3a8a;margin-bottom:6px;">Case law construing this statute</div>` +
    offline +
    external +
    `</div>`
  );
}

/* --------------------------------------- small global helpers for the panel */

/** Open an opinion by doc id (used by the Cited-by list). */
export function openOpinionById(docId: string): void {
  void import('./law-opinions').then((m) => m.openOpinion(docId));
}
/** Open a rule by doc id (used by the Cited-by list). */
export function openRuleById(docId: string): void {
  void import('./law-rules').then((m) => m.openRule(docId));
}
